/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import {
  AccountListInitialProperties,
  AccountListInterface,
  AccountListResponseInterFace,
  RequestAccountListInterFace,
} from '../../interface/account-list-penalty-interface';
import { CommonFilterProperties, FilterUpdateProperties } from '../../interface/filter.interface';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import penaltyManagementApi from '../../utils/api/penalty-management';


export const fetchAccountsList = createAsyncThunk<
  AccountListResponseInterFace,
  RequestAccountListInterFace
>('account/fetchAccountsListFilters', async (data: RequestAccountListInterFace, { dispatch }) => {
  try {
    const response = await penaltyManagementApi.fetchPenaltyManagementAccount(
      removeEmptyKeys({ ...data }),
    );
    return response.data;
  } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
    throw error;
  }
});

export const fetchAccountListFilter = createAsyncThunk<CommonFilterProperties[], void>(
  'account/fetchAccountListFilter',
  async (data, { dispatch }) => {
    try {
      const response = await penaltyManagementApi.accountFiltersList({ data });
      const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
      return updatedResponse;
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationMessage',
        payload: {
          type: 'error',
          message: error?.response?.data?.message || 200,
          code: error?.response?.data?.code || 'Something went wrong!',
          status: true,
        },
      });
      throw error;
    }
  },
);

const availableFilters: CommonFilterProperties[] = [
  {
    name: 'Search here...',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: AccountListInitialProperties = {
  accountList: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: availableFilters,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
};

export const AccountListPenalty = createSlice({
  name: 'account-penalty',
  initialState,
  reducers: {
    /**
     * Update By month filters
     * @param state
     * @param action
     */
    updateAccountListFilter: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * function for set sortBy
     * @param state
     * @param action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
    /**
     * single Updated account list data
     */
    updatedSingleAccount: (
      state: AccountListInitialProperties,
      action: PayloadAction<AccountListInterface>,
    ) => {
      const updatedAccount = action.payload; // account to be added at the top
      const accountListData = state.accountList; // Assign the existing account to another variable

      // Find the index of the updated account by ID
      const index = accountListData.findIndex(
        (eachAccount) => eachAccount._id === updatedAccount._id,
      );
      // If the index found, replace the entire data
      if (index !== -1) {
        accountListData[index] = {
          ...updatedAccount,
        };
      }
      state.accountList = accountListData;
    },
  },
  /**
   * Use the extra reducers function
   * @param builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<AccountListInitialProperties>) => {
    builder
      .addCase(fetchAccountsList.pending, (state: AccountListInitialProperties) => {
        state.loading = true;
      })
      .addCase(
        fetchAccountsList.fulfilled,
        (
          state: AccountListInitialProperties,
          action: PayloadAction<AccountListResponseInterFace>,
        ) => {
          state.loading = false;
          state.accountList = [...action.payload.results];
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchAccountsList.rejected, (state: AccountListInitialProperties) => {
        state.loading = false;
        // Handle error if needed
      });

    builder
      .addCase(fetchAccountListFilter.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchAccountListFilter.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = [...(state.filters || []), ...action.payload];
        },
      )
      .addCase(fetchAccountListFilter.rejected, (state) => {
        state.filterListLoading = false;
      });
  },
});

// Export actions
export const { updateAccountListFilter, setSortData, updatedSingleAccount } =
  AccountListPenalty.actions;
// Export the reducer
export default AccountListPenalty.reducer;
